import { duration } from '../utils/duration';
import { getRandomInt } from '../utils/random';

/** Theme for the feature */
export type StreakBattleRoyaleTheme = 'lightning';

/** Default theme for new streak events */
export const STREAK_EVENT_DEFAULT_THEME: StreakBattleRoyaleTheme = 'lightning';

/** Minimum match2 puzzle level required to unlock the streak event */
export const STREAK_EVENT_LEVEL_REQUIREMENT = 66;

/**
 * Streak event duration in milliseconds after successfully joined.
 */
export const STREAK_EVENT_DURATION = duration({ hours: 24 });

/**
 * UTC hours at which the streak event resets.
 * In the format of [hour, minute, second, millisecond].
 */
export const STREAK_EVENT_UTC_RESET_HOURS: [number, number, number, number] = [
  18, 0, 0, 0,
];

/** Progression Logic type for the feature */
export type StreakBattleRoyaleProgression = 'basic' | 'gaussian';

/** Default progression logic type for new streak events */
export const STREAK_EVENT_DEFAULT_PROGRESSION: StreakBattleRoyaleProgression =
  'gaussian';

/** Getters for player number ratio for each milestone/checkpoint */
export const STREAK_EVENT_PLAYERS_PROGRESSION: ((
  random: () => number,
) => number)[] = [
  (random) => getRandomInt(80, 95, random), // Level 1
  (random) => getRandomInt(45, 80, random), // Level 2
  (random) => getRandomInt(25, 45, random), // Level 3
  (random) => getRandomInt(20, 25, random), // Level 4
  (random) => getRandomInt(12, 20, random), // Level 5
  (random) => getRandomInt(7, 12, random), // Level 6
  (random) => getRandomInt(1, 7, random), // Level 7 (Summit)
];
export const STREAK_EVENT_PLAYERS_PROGRESSION_GAUSSIAN: ((
  random: () => number,
) => number)[] = [
  (random) => getGaussianClamped(90, 98, 94, 2, random), // Level 1
  (random) => getGaussianClamped(75, 95, 88, 4, random), // Level 2
  (random) => getGaussianClamped(60, 85, 75, 5, random), // Level 3
  (random) => getGaussianClamped(50, 70, 60, 4, random), // Level 4
  (random) => getGaussianClamped(30, 50, 40, 4, random), // Level 5
  (random) => getGaussianClamped(10, 30, 20, 4, random), // Level 6
  (random) => getGaussianClamped(1, 10, 5, 2, random), // Level 7 (Summit)
];

function getGaussianClamped(
  min: number,
  max: number,
  mean: number,
  stdDev: number,
  random: () => number,
): number {
  let u = 0,
    v = 0;
  while (u === 0) u = random();
  while (v === 0) v = random();
  let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  num = num * stdDev + mean;
  return Math.min(max, Math.max(min, num));
}

/** Amount of streak milestones needed to complete the event */
export const STREAK_EVENT_GOAL = STREAK_EVENT_PLAYERS_PROGRESSION.length;

/** Amount of gems rewarded for completing the streak event, splitted among the winners */
export const STREAK_EVENT_REWARD_GEMS_POOL = 2000;

/** Amount of players in the event, both fakes and the player himself/herself */
export const STREAK_EVENT_PLAYER_COUNT = 100;

/** Streak event possible statuses */
export type StreakBattleRoyaleStatus =
  | 'available'
  | 'active'
  | 'failed'
  | 'expired'
  | 'completed'
  | 'rewarded';
